import React, { useEffect, useRef } from "react";
import phone from "../assets/taskManagerImg3.png";
import styled, { keyframes } from "styled-components";
import { motion } from "framer-motion";

const Tasks = () => {
  const containerRef = useRef(null);
  const imageRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current || !imageRef.current) return;

      const container = containerRef.current;
      const rect = container.getBoundingClientRect();
      const isVisible = rect.top < window.innerHeight * 0.8 && rect.bottom > 0;

      if (isVisible) {
        const scrollPercent =
          1 - Math.max(0, rect.top) / (window.innerHeight * 0.8);
        imageRef.current.style.transform = `translateY(${
          -20 + scrollPercent * 20
        }px) scale(${1 + scrollPercent * 0.05})`;
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <Container ref={containerRef}>
      <GlassBackground />
      <ContentWrapper>
        {/* <ImageGlow /> */}
        <PhoneImage src={phone} alt="Task Manager App Interface" />
        {/* <ImageContainer ref={imageRef}>
          <FloatingElements>
            <FloatingDot
              style={{ top: "20%", left: "15%", animationDelay: "0s" }}
            />
            <FloatingDot
              style={{ top: "60%", right: "10%", animationDelay: "1s" }}
            />
            <FloatingDot
              style={{ bottom: "30%", left: "20%", animationDelay: "2s" }}
            />
          </FloatingElements>
        </ImageContainer> */}

        <TextContent>
          <TitleWrapper>
            <PreTitle>INTUITIVE WORKFLOW</PreTitle>
            <MainTitle>
              Organize Your
              <HighlightedWord>
                <span className="highlight-text">Work</span>
                <HighlightUnderline />
              </HighlightedWord>
              Effortlessly
            </MainTitle>
          </TitleWrapper>

          <Description>
            Streamline your workflow with our intelligent task management
            system. Prioritize tasks, set deadlines, and collaborate
            seamlessly—all in one beautifully designed interface that adapts to
            your working style.
          </Description>

          <FeatureList>
            <FeatureItem>
              <FeatureIcon>✓</FeatureIcon>
              <span>Smart task prioritization</span>
            </FeatureItem>
            <FeatureItem>
              <FeatureIcon>✓</FeatureIcon>
              <span>Real-time collaboration</span>
            </FeatureItem>
            <FeatureItem>
              <FeatureIcon>✓</FeatureIcon>
              <span>Cross-platform sync</span>
            </FeatureItem>
          </FeatureList>

          <ButtonGroup>
            <PrimaryButton
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <ButtonText>Start Free Trial</ButtonText>
              <ButtonArrow>→</ButtonArrow>
            </PrimaryButton>
            <SecondaryButton whileHover={{ x: 5 }}>
              <span>Watch Demo</span>
              <PlayIcon>▶</PlayIcon>
            </SecondaryButton>
          </ButtonGroup>

          <StatsContainer>
            <Stat>
              <StatNumber>98%</StatNumber>
              <StatLabel>User Satisfaction</StatLabel>
            </Stat>
            <StatDivider />
            <Stat>
              <StatNumber>24/7</StatNumber>
              <StatLabel>Support Available</StatLabel>
            </Stat>
            <StatDivider />
            <Stat>
              <StatNumber>10K+</StatNumber>
              <StatLabel>Active Teams</StatLabel>
            </Stat>
          </StatsContainer>
        </TextContent>
      </ContentWrapper>
    </Container>
  );
};

// Animations
const floatAnimation = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(146, 129, 221, 0.4); }
  70% { box-shadow: 0 0 0 20px rgba(146, 129, 221, 0); }
  100% { box-shadow: 0 0 0 0 rgba(146, 129, 221, 0); }
`;

const slideIn = keyframes`
  from {
    width: 0;
  }
  to {
    width: 100%;
  }
`;

// Styled Components
const Container = styled.section`
  padding: 10em 5%;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%);
  min-height: 100vh;
  display: flex;
  align-items: center;

  @media (max-width: 1024px) {
    padding: 6em 5%;
  }
`;

const GlassBackground = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80%;
  height: 80%;
  background: radial-gradient(
    circle at 30% 30%,
    rgba(146, 129, 221, 0.1) 0%,
    transparent 50%
  );
  filter: blur(60px);
  z-index: 0;
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8rem;
  align-items: center;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 4rem;
    text-align: center;
  }
`;

const ImageContainer = styled.div`
  position: relative;
  animation: ${fadeInUp} 1s ease-out;
  transition: transform 0.3s ease-out;

  @media (max-width: 1024px) {
    order: 2;
  }
`;

const PhoneImage = styled.img`
  width: 100%;
  max-width: 600px;
  object-fit: contain;
  max-height: 90vh;
  border-radius: 32px;
  box-shadow: 0 40px 80px rgba(0, 0, 0, 0.1),
    0 20px 40px rgba(146, 129, 221, 0.2),
    inset 0 1px 1px rgba(255, 255, 255, 0.5);
  transform: perspective(1000px) rotateY(-10deg) rotateX(5deg);
  transition: transform 0.5s ease;

  &:hover {
    transform: perspective(1000px) rotateY(0deg) rotateX(0deg) scale(1.02);
  }

  @media (max-width: 768px) {
    max-width: 400px;
    margin: 0 auto;
  }
  @media (max-height: 900px) {
    max-height: 560px;
  }
`;

const ImageGlow = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 105%;
  height: 105%;
  background: linear-gradient(
    45deg,
    rgba(146, 129, 221, 0.3),
    rgba(100, 88, 188, 0.1)
  );
  border-radius: 40px;
  filter: blur(20px);
  z-index: -1;
  opacity: 0.6;
  animation: ${pulse} 4s infinite;
`;

const FloatingElements = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

const FloatingDot = styled.div`
  position: absolute;
  width: 8px;
  height: 8px;
  background: linear-gradient(45deg, #9281dd, #6d5bd4);
  border-radius: 50%;
  animation: ${floatAnimation} 3s ease-in-out infinite;
`;

const TextContent = styled.div`
  animation: ${fadeInUp} 1s ease-out 0.2s both;
`;

const TitleWrapper = styled.div`
  margin-bottom: 2.5rem;
`;

const PreTitle = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: #9281dd;
  margin-bottom: 0.5em;
  opacity: 0.9;
`;

const MainTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 300;
  line-height: 1.1;
  color: #1a1a2e;
  margin-bottom: 0rem;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI",
    sans-serif;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HighlightedWord = styled.span`
  position: relative;
  display: inline-block;
  margin: 0 8px;
  font-weight: 700;
  color: #9281dd;
`;

const HighlightUnderline = styled.span`
  position: absolute;
  bottom: 5px;
  left: 0;
  width: 100%;
  height: 8px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(146, 129, 221, 0.3),
    transparent
  );
  border-radius: 4px;
  animation: ${slideIn} 1.5s ease-out;
`;

const Description = styled.p`
  font-size: 1rem;
  line-height: 1.4;
  color: #9da0a7;
  margin-bottom: 2rem;
  max-width: 500px;
  opacity: 0.9;

  @media (max-width: 1024px) {
    margin: 0 auto 3rem;
  }
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 3rem;
  max-width: 500px;

  @media (max-width: 1024px) {
    margin: 0 auto 3rem;
  }
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 1rem;
  color: #2d3748;
  opacity: 0;
  animation: ${fadeInUp} 0.6s ease-out forwards;

  &:nth-child(1) {
    animation-delay: 0.4s;
  }
  &:nth-child(2) {
    animation-delay: 0.6s;
  }
  &:nth-child(3) {
    animation-delay: 0.8s;
  }

  &:hover {
    transform: translateX(5px);
    transition: transform 0.3s ease;
  }
`;

const FeatureIcon = styled.span`
  width: 24px;
  height: 24px;
  background: linear-gradient(135deg, #9281dd, #6d5bd4);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: bold;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const ButtonBase = styled(motion.button)`
  padding: 1.2rem 2.5rem;
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: "Inter", sans-serif;
`;

const PrimaryButton = styled(ButtonBase)`
  background: linear-gradient(135deg, #9281dd, #6d5bd4);
  color: white;
  box-shadow: 0 10px 30px rgba(146, 129, 221, 0.3);

  &:hover {
    box-shadow: 0 15px 40px rgba(146, 129, 221, 0.4);
  }
`;

const SecondaryButton = styled(ButtonBase)`
  background: transparent;
  color: #4a5568;
  border: 2px solid #e2e8f0;

  &:hover {
    background: rgba(146, 129, 221, 0.05);
    border-color: #9281dd;
  }
`;

const ButtonText = styled.span`
  letter-spacing: 0.5px;
`;

const ButtonArrow = styled.span`
  font-size: 1.2rem;
  transition: transform 0.3s ease;
  ${PrimaryButton}:hover & {
    transform: translateX(5px);
  }
`;

const PlayIcon = styled.span`
  font-size: 0.875rem;
  color: #9281dd;
`;

const StatsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  flex-wrap: wrap;
  padding-top: 1rem;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  max-width: 500px;

  @media (max-width: 768px) {
    justify-content: center;
    gap: 1.5rem;
  }
`;

const Stat = styled.div`
  text-align: center;
`;

const StatNumber = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #1a1a2e;
  background: linear-gradient(135deg, #9281dd, #6d5bd4);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 0.25rem;
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: #718096;
  font-weight: 500;
`;

const StatDivider = styled.div`
  width: 1px;
  height: 40px;
  background: linear-gradient(to bottom, transparent, #e2e8f0, transparent);

  @media (max-width: 768px) {
    display: none;
  }
`;

export default Tasks;
